/**
 * Recording additions of the November 2026 redesign (docs/redesign-2026-11/README.md, Backend contract › Realdata;
 * contracts/ego.md §5), hand-written until realdata's types are generated:
 *
 * - Every label points to its proof (bookmark ids); the place is an episode field, so its proof is
 *   `environment_evidence`.
 * - What counts as proof, and what a recording needs before Finish, are the server's rules: they come back as
 *   `problems` (ego.ts `RecordingProblem`) and as the served `annotation_rules`, never as a client copy.
 */
import { DEFAULT_ANNOTATION_RULES, provesUnder, type AnnotationRules } from './app-config.ts'
import type { AnnotationDoc, Episode, EvidenceBookmark, LabelRole, MyCapture } from './ego.ts'

/** An episode with the place's proof. */
export interface EpisodeWithProof extends Episode {
  environment_evidence?: string[]
}

export interface AnnotationDocWithProof extends Omit<AnnotationDoc, 'episodes'> {
  episodes: EpisodeWithProof[]
}

/** What kind of proof a label takes: a frame for a place or an object (an object may use a clip), a clip otherwise. */
export function proofKind(role: LabelRole | 'environment'): 'frame' | 'clip' {
  return role === 'environment' || role === 'object' ? 'frame' : 'clip'
}

/** Whether one bookmark fits a label in this role under the recording's served rules (today's when none were
 * served): for drawing the proof a label shows, never for refusing it. */
export function provesRole(role: LabelRole | 'environment', b: Pick<EvidenceBookmark, 'dur_ms'>, rules: AnnotationRules = DEFAULT_ANNOTATION_RULES): boolean {
  return provesUnder(rules, role, b.dur_ms)
}

/** Capture summary fields the recordings table reads. */
export interface MyCaptureRow extends MyCapture {
  /** The server's words for a status this build doesn't know. */
  status_label?: string | null
}
